import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { filterSearch } from "@/lib/columnFilters";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { StorageLocationsClient } from "@/features/warehouse/StorageLocationsClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";
import { listPlaces, warehouseKeys, type PlacesSearch } from "@/features/warehouse/warehouse.api";

// Warehouse → Plekken (docs/design_handoff_wms/README.md §4.1). Reading is wms_masterdata;
// adding, blocking and changing places is wms_masterdata_write — in the screen, and on the API
// regardless.
export const Route = createFileRoute("/_shell/warehouse/locations")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_masterdata") && !hasModule(context.session, "wms_masterdata_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): PlacesSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    return {
      ...filterSearch(search),
      warehouse: str(search.warehouse),
      zone: str(search.zone),
      aisle: str(search.aisle),
      rack: str(search.rack),
      group: str(search.group),
      q: str(search.q),
      cursor: str(search.cursor),
      limit: str(search.limit),
      sort: str(search.sort),
      order: str(search.order),
    };
  },
  component: StorageLocationsScreen,
});

function StorageLocationsScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canEdit = session.roles.includes("admin") || hasModule(session, "wms_masterdata_write");

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  // Another warehouse is another tree: the node and the page start over.
  const choice = useWarehouseChoice(search.warehouse, (code) =>
    updateParams({ warehouse: code, zone: "", aisle: "", rack: "", group: "", cursor: "", q: "" }),
  );
  const warehouse = choice.current;

  const query = useQuery({
    queryKey: warehouseKeys.places(warehouse?.id ?? "", search),
    queryFn: () => listPlaces(warehouse!.id, search),
    enabled: warehouse !== undefined,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Warehouse Locations">
      {choice.error ? (
        <div className="wl-alert wl-alert--danger">
          <span>{choice.error instanceof Error ? choice.error.message : t("common.errorGeneric")}</span>
        </div>
      ) : !warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : (
        <StorageLocationsClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          items={query.data?.data ?? []}
          pagination={query.data?.pagination ?? { limit: 50, hasNextPage: false, nextCursor: null }}
          search={search}
          updateParams={updateParams}
          userId={session.userId}
          canEdit={canEdit}
          isAdmin={session.roles.includes("admin")}
          canSeeStock={session.roles.includes("admin") || hasModule(session, "wms_stock") || hasModule(session, "wms_stock_write")}
          isLoading={query.isLoading}
          isFetching={query.isFetching}
        />
      )}
    </div>
  );
}
